import moment from "moment";
import { action, computed, observable, runInAction } from "mobx";
import { compact, isEmpty, toString } from "lodash";
import {
  BookingSupportStatusDto,
  TimereportDto,
  TimereportStatus,
  TimereportTrackingDto
} from "@api/timereports";
import { store } from "@store";
import { TimereportDayFormStore } from "./TimereportDayForm/TimereportDayFormStore";
import { translateSendTimereportError } from "./translate-send-timereport-error";
import {
  BookingInfoForTimeReportFragment,
  BookingKitProfileFragment,
  TimeReport_GetBookingInfo,
  TimeReport_GetCustomerContacts,
  TimeReport_SendToCustomer as sendEmail,
  TimeReportStatus,
  TimeReportTemplateType,
  GetTimereportTemplates_ByCustomerId,
  SendTimeReportToConsultant,
  TimeReport_ClearDays,
  Timereport_GetByBookingId,
  Timereport_GetFreeTimereports,
  Timereport_GetStatusHistory,
  Timereport_GetSendFailures,
  Timereport_GetInvoiceStatusForBooking,
  Timereport_AssignToBooking,
  Timereport_Approve,
  Timereport_SetToSubmitted,
  Timereport_SetToDraft
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { OkCancelDialogOptions } from "@containers";
import { SendFailures } from "./TimeReportStatus";
import { ObservableQuery, ObservableSubscription } from "@apollo/client";
import {
  getBody,
  getRecipients,
  getSubject
} from "@bookings/BookingProfile/TimeReport/utils";
import {
  replaceEmailDomainByTenant,
  Tenant,
  TimereportTemplateConfig
} from "@bonliva-crm/shared";
import { DropdownButtonOption } from "@common/DropdownButton";

import { logSubscriptionError } from "@utils/log-subscription-error";
import { ownsDay as ownsMonthBreakDay } from "./month-break-days";
import { unsavedDaysWarning } from "./unsaved-days-warning";
export class TimeReportStore {
  dayFormStores: TimereportDayFormStore[];
  private bookingInfoQuery: ObservableQuery<
    TimeReport_GetBookingInfo.Return,
    TimeReport_GetBookingInfo.Variables
  >;

  private bookingInfoSubscription: ObservableSubscription;

  @observable loading = true;
  // The booking carries which side of a month-break week this is, and that
  // decides which days may be edited. Rendering the week before it lands would
  // offer every day as editable and then take some away again.
  @observable bookingInfoLoaded = false;
  // The booking failed to load. Its month-break side is what says which days
  // this booking may hold, so without it the week is shown read-only rather
  // than offering days the API would refuse on save.
  @observable bookingInfoFailed = false;
  @observable skipReminder = false;
  @observable isNationalProcurement = false;
  @observable type = TimeReportTemplateType.Basic;
  @observable availableTypes = [TimeReportTemplateType.Basic];
  @observable bookingInfo: BookingInfoForTimeReportFragment = null;
  @observable
  contactPersons: TimeReport_GetCustomerContacts.Return["contacts"] = null;
  @observable timeReportData: TimereportDto = null;
  @observable statusHistory: TimereportTrackingDto = null;
  // Recipients the last send-to-customer attempt never reached. Kept apart from
  // statusHistory because that DTO comes from timereport-api, which only knows
  // about sends that succeeded (CRMDEV-7141).
  @observable sendFailures: SendFailures = null;
  @observable error = null;
  @observable timereportNotFound = false;
  @observable invoicePaymentStatuses: BookingSupportStatusDto = null;

  @observable loadingClearTR = false;
  @observable loadingFreeTimereports = true;
  @observable loadingSetDraft = false;
  @observable freeTimereports: TimereportDto[] = [];

  @observable loadingApprove = false;
  @observable loadingSubmit = false;
  @observable loadingEmail = false;
  @observable emailDialogOpen = false;
  @observable loadingTemplates = true;
  @observable needSignatureTROkInApp = false;

  toggleEmailDialog = () => {
    this.emailDialogOpen = !this.emailDialogOpen;
  };

  /**
   * True until everything the week's day rules depend on has arrived: the
   * timereport itself and the booking that says which month-break side it is.
   */
  @computed get isLoading() {
    return this.loading || !this.bookingInfoLoaded;
  }

  @computed get showFreeTR() {
    return (
      this.timeReportData &&
      (this.timeReportData.status === TimereportStatus.Disabled ||
        this.timeReportData.status === TimereportStatus.Draft ||
        this.timeReportData.status === TimereportStatus.Blank) &&
      this.freeTimereports?.length > 0
    );
  }

  @computed get trStatus() {
    return this.timeReportData.status as TimeReportStatus | undefined;
  }

  @computed get isDraft() {
    return this.trStatus === TimeReportStatus.Draft;
  }

  @computed get isDraftOrBlank() {
    return [TimeReportStatus.Draft, TimeReportStatus.Blank].includes(this.trStatus);
  }

  @computed get bookingKitSupportTags() {
    return this.bookingInfo?.bookingSupportTagStatus?.map((x) => x.tag) ?? [];
  }
  @computed get bookingKitSupportComment() {
    return this.bookingInfo?.bookingSupportTagComment;
  }

  @computed get warningMsg() {
    if (this.timeReportData.status === TimereportStatus.Draft) {
      return locale.WarningTRBeforeAttachDraft;
    }
    if (this.timeReportData.status === TimereportStatus.Blank) {
      return locale.WarningTRBeforeAttachBlank;
    }
  }

  @computed get timeReportViewLink() {
    return `/time-report/${this.bookingId}/view/${this.type}`;
  }

  @computed get timeReportDownloadLink() {
    return `/time-report/${this.bookingId}/pdf/${this.type}`;
  }

  constructor(
    public bookingId: string,
    public consultantId: string,
    public week: number,
    public year: number,
    public tenant: Tenant
  ) {
    this.fetch(bookingId);
    this.fetchFreeTimereports();
    this.fetchCustomerInvoiceStatus(bookingId);
    this.getBookingInfo(bookingId);
    store().localPubSub.timereportUpdated.on((args) => {
      this.timeReportData.status = args.status;
    });
    (window as any).timeReportStore = this;
  }

  getBookingInfo = async (bookingId) => {
    this.bookingInfoQuery = TimeReport_GetBookingInfo.funcWatch(
      {
        id: bookingId
      },
      { fetchPolicy: "network-only" }
    );
    this.bookingInfoSubscribe();
  };

  bookingInfoSubscribe() {
    this.bookingInfoUnSubscribe();

    this.bookingInfoSubscription = this.bookingInfoQuery.subscribe({
      next: ({ data, loading }) => {
        // Apollo emits a partial result while the request is still in flight.
        // Counting that as loaded would show the week before the booking says
        // which month-break side it is, and every day would read as editable
        // for as long as the real answer took to arrive.
        if (data?.bookingInfo || !loading) {
          this.bookingInfoLoaded = true;
        }

        if (data) {
          return this.applyBookingInfo(data.bookingInfo);
        }
      },
      error: (error) => {
        // A booking that cannot be read must not hold the week hostage — it is
        // still shown, read-only, because without the booking there is nothing
        // to say which days this side of a month-break week owns.
        this.bookingInfoLoaded = true;
        this.bookingInfoFailed = !this.bookingInfo;
        logSubscriptionError("TimeReportStore")(error);
      }
    });
  }

  private applyBookingInfo(bookingInfo: BookingInfoForTimeReportFragment) {
    this.bookingInfo = bookingInfo;
    this.skipReminder = this.bookingInfo?.skipTimereportReminder;
    this.isNationalProcurement = this.bookingInfo?.BookingGroup.isNationalProcurement;
    this.bookingInfoFailed = !this.bookingInfo;

    if (!this.bookingInfo) {
      return;
    }

    GetTimereportTemplates_ByCustomerId.func({
      customerId: this.bookingInfo.BookingGroup.hospital._id
    })
      .then(({ data }) => {
        if (data) {
          this.needSignatureTROkInApp = data.customerById?.needSignatureTROkInApp;
          const profession = store().cache.getProfessionsOnAreasofExp([
            this.bookingInfo.AreaOfExpertise
          ])?.[0];
          const template = new TimereportTemplateConfig(
            data.customerById,
            data.customerById?.timeReportTemplateSettings
          ).getType(profession, false);
          this.type = template || TimeReportTemplateType.Basic;
          this.availableTypes = compact([TimeReportTemplateType.Basic, template]);
        }
      })
      .finally(() => (this.loadingTemplates = false));

    return TimeReport_GetCustomerContacts.func({
      hospId: this.bookingInfo.BookingGroup.hospital._id,
      depIds: this.bookingInfo.BookingGroup.department
        ? [this.bookingInfo.BookingGroup.department._id]
        : []
    }).then(({ data }) => {
      if (data) {
        this.contactPersons = data.contacts;
      }
    });
  }

  /**
   * A failed subscription is finished — Apollo delivers the error and stops —
   * so the retry reads the answer off `refetch` itself and only then starts
   * watching again.
   */
  retryBookingInfo = () => {
    this.bookingInfoQuery
      ?.refetch()
      .then(({ data }) => {
        if (!data?.bookingInfo) {
          return;
        }

        this.applyBookingInfo(data.bookingInfo);
        this.bookingInfoSubscribe();
      })
      .catch(() => {
        this.bookingInfoFailed = !this.bookingInfo;
      });
  };

  bookingInfoUnSubscribe() {
    this.bookingInfoSubscription?.unsubscribe();
  }

  /**
   * Reads the week back from the service after an action has sent it on, the way GODKÄNN
   * does after the status change. Until then unsaved edits stay on screen as if they had
   * gone with it (CRMDEV-7588).
   */
  reload = () => {
    this.fetch(this.bookingId);
  };

  /** Reads the week from the service; resolves once the days on screen are replaced. */
  fetch(bookingId: string) {
    this.loading = true;
    this.error = null;
    return Timereport_GetByBookingId.func({ bookingId }, { fetchPolicy: "network-only" })
      .then((result) => {
        runInAction(() => {
          const _tr = result.data?.getTimereportByBookingId.timereport;
          this.timeReportData = _tr;
          this.loading = false;
          this.dayFormStores = [];

          if (!_tr?.days) {
            this.timereportNotFound = true;
            return;
          }

          if (process.env.NODE_ENV === "development") {
            console.log(`timereport id: ${this.timeReportData.timereportId}`);
          }

          // TODO: remove this after full workTimeRanges migration
          _tr.days.forEach(({ workTimeRange, ...day }) => {
            const initial = {
              ...day,
              mileage: toString(day.mileage).replace(".", ",")
            };

            const store = new TimereportDayFormStore(
              this.timeReportData.id,
              initial,
              () => this.fetchStatusHistory(),
              this.bookingId,
              () => this.ownsDay(day.date)
            );
            this.dayFormStores.push(store);
          });
          //TODO change to 1 endpoint
          this.fetchStatusHistory();
        });
      })
      .catch((e) => {
        console.log({ e });

        runInAction(() => {
          this.loading = false;

          if (e.status === 404) {
            this.timereportNotFound = true;
          } else {
            this.error = e.toString();
          }
        });
      });
  }

  fetchStatusHistory = () => {
    Timereport_GetStatusHistory.func(
      { bookingId: this.bookingId },
      { fetchPolicy: "network-only" }
    )
      .then((res) => {
        this.statusHistory = res.data?.getTimereportStatusHistory.history;
      })
      .catch((err) => {
        store().snackbars.error(`Cannot get timereport status history`);
      });

    this.fetchSendFailures();
  };

  fetchSendFailures = () => {
    Timereport_GetSendFailures.func(
      { bookingId: this.bookingId },
      { fetchPolicy: "network-only" }
    )
      .then((res) => {
        this.sendFailures = res.data?.getTimereportSendFailures ?? null;
      })
      .catch(() => {
        // A missing failure list must not take the whole status panel down —
        // the panel is still correct without it.
        this.sendFailures = null;
      });
  };

  fetchCustomerInvoiceStatus = (bookingId: string) => {
    Timereport_GetInvoiceStatusForBooking.func(
      { bookingId },
      { fetchPolicy: "network-only" }
    )
      .then((res) => {
        this.invoicePaymentStatuses =
          res.data?.getInvoiceStatusForBooking.invoicePaymentStatuses;
      })
      .catch((err) => {
        store().snackbars.error(`Cannot get customer invoice status history`);
      });
  };

  fetchFreeTimereports = () => {
    Timereport_GetFreeTimereports.func(
      {
        consultantId: this.consultantId,
        week: this.week,
        year: this.year
      },
      { fetchPolicy: "network-only" }
    )
      .then((result) => {
        runInAction(() => {
          this.loadingFreeTimereports = false;
          this.freeTimereports = result.data?.getFreeTimereports.freeTimereports.filter(
            (x) => !x.isAssignedToBooking
          );
        });
      })
      .catch((err) => {
        this.loadingFreeTimereports = false;
        store().snackbars.error(`Cannot get consultant free timereports`);
      });
  };

  attachToTimereport = (timereportId) => {
    this.loadingFreeTimereports = true;
    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions(this.warningMsg))
      .then((res) => {
        if (res === "ok") {
          Timereport_AssignToBooking.func({
            bookingId: this.bookingId,
            timeReportId: timereportId
          })
            .then(() => {
              store().snackbars.success(`Timereport successfully attached`);

              this.fetch(this.bookingId);
              this.fetchFreeTimereports();
            })
            .catch((err) => {
              store().snackbars.error(`Cannot attach timereport`);
            });
        }
      })
      .finally(() => {
        this.loadingFreeTimereports = false;
      });
  };

  clearDaysTimereport = () => {
    this.loadingClearTR = true;
    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions("Är du säker?"))
      .then((res) => {
        if (res === "ok") {
          TimeReport_ClearDays.func({
            timeReportId: this.timeReportData?.id
          })
            .then(() => {
              store().snackbars.success(`Timereport successfully cleared`);
              this.fetch(this.bookingId);
            })
            .catch((err) => {
              store().snackbars.error(`Cannot clear timereport ${err}`);
            });
        }
      })
      .finally(() => {
        this.loadingClearTR = false;
      });
  };

  /**
   * A week the consultant withdrew in the app. It stays visible with its status
   * history, but it no longer counts as hours worked — so nothing may be
   * written to it and it cannot be moved on to another status.
   */
  @computed get isDeletedByConsultant() {
    return this.timeReportData?.status === TimereportStatus.Disabled;
  }

  @computed get isDisabledApprove() {
    return (
      this.isDeletedByConsultant ||
      !store().user.can("timereport:approve:all") ||
      this.timeReportData?.status === TimereportStatus.ApprovedByCustomer ||
      this.timeReportData?.status === TimereportStatus.ConsultantPaymentProcessed ||
      this.loadingApprove
    );
  }

  @computed get isDisabledSubmit() {
    return (
      this.isDeletedByConsultant ||
      !store().user.can("timereport:approve:all") ||
      this.timeReportData?.status === TimereportStatus.SubmittedByConsultant ||
      this.timeReportData?.status === TimereportStatus.ApprovedByCustomer ||
      this.timeReportData?.status === TimereportStatus.ConsultantPaymentProcessed ||
      this.loadingSubmit
    );
  }

  @computed get canAccessTemplates() {
    return store().user.can("timereport-templates:read:all");
  }

  @computed get optionsTRTemplates() {
    return this.canAccessTemplates
      ? Object.values(TimeReportTemplateType)
      : this.availableTypes;
  }

  @computed get isDisabledSetSkipReminder() {
    return (
      this.isDeletedByConsultant ||
      !store().user.can("timereport-reminder:set-skip-reminder:all")
    );
  }

  @computed get rejectedKits(): BookingKitProfileFragment[] {
    return this.bookingInfo?.rejectedBookingKits ?? [];
  }

  @computed get isDisabledSendHBA() {
    return (
      this.timeReportData?.status !== TimereportStatus.ApprovedByCustomer ||
      this.bookingInfo?.rejectedBookingKits.length > 0
    );
  }

  /**
   * Whether this booking is the side of a month-break week that holds the given
   * day. The other side's days are shown but not editable: the consultant's app
   * reads each day from the booking that owns it, so hours entered on the wrong
   * side never reach them.
   */
  ownsDay = (date: Date | string) =>
    ownsMonthBreakDay(this.bookingInfo?.Week, this.bookingInfo?.monthBreakSide, date);

  @computed get isHiddenActionBar() {
    return (
      this.bookingInfoFailed ||
      (this.timeReportData?.status !== TimereportStatus.Draft &&
        this.timeReportData?.status !== TimereportStatus.Blank)
    );
  }

  @computed get canEditExpenses() {
    if (this.bookingInfoFailed) {
      return false;
    }

    return (
      this.timeReportData?.status === TimereportStatus.Draft ||
      this.timeReportData?.status === TimereportStatus.Blank ||
      this.timeReportData?.status === TimereportStatus.ApprovedByCustomer
    );
  }

  @computed get canEditOnlyMiles() {
    return (
      !this.bookingInfoFailed &&
      this.timeReportData?.status === TimereportStatus.ApprovedByCustomer
    );
  }

  @computed get isEmptyTR() {
    return this.timeReportData?.days.every(
      (x) =>
        isEmpty(x.vabTimeShifts) && isEmpty(x.sickTimeShifts) && isEmpty(x.workTimeRanges)
    );
  }

  @computed get hasCustomerInvoice() {
    return !!this.invoicePaymentStatuses?.invoiceFormExists;
  }

  @computed get hasSubcontractorForm() {
    return !!this.invoicePaymentStatuses?.subcontractorFormExists;
  }

  @computed get hasConsultantInvoice() {
    return !!this.invoicePaymentStatuses?.paymentFormExists;
  }

  @computed get hasSickPay() {
    return !!this.invoicePaymentStatuses?.sickFormExists;
  }

  @computed get canSetToDraft() {
    const hasTimeReport =
      !!this.timeReportData &&
      this.timeReportData.status !== TimereportStatus.Draft &&
      !this.isDeletedByConsultant;

    // true if there exists at least one non-deleted kit
    const hasActiveKits =
      this.bookingInfo?.bookingKits?.some((kit) => kit.deleted === false) ?? false;

    return hasTimeReport && !hasActiveKits;
  }

  @computed get templateTypeOptions() {
    return this.optionsTRTemplates.map((x, i) => ({
      label: locale.timeReportTemplateType[x],
      value: x,
      color: "secondary" as const,
      tabIndex: i
      // leftIcon: <Email />
    }));
  }

  getSelectedTemplateTypeIndex(value: any, options?: DropdownButtonOption[]) {
    return options?.findIndex((x) => x.value === value);
  }

  /**
   * Runs `next` straight away, or after an OK when a day still holds edits (CRMDEV-7588).
   * These actions have no confirm of their own to carry the warning. Each one reloads the
   * week once it has gone through (`reload`), which is when the dropped edits leave the
   * screen; Cancel keeps them.
   */
  confirmUnsavedDays = (next: () => void) => {
    const warning = unsavedDaysWarning(this.dayFormStores);

    if (!warning) {
      next();
      return;
    }

    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions(undefined, warning))
      .then((res) => {
        if (res === "ok") {
          next();
        }
      });
  };

  handleApproveStatus = (onSuccess: () => void) => {
    this.loadingApprove = true;
    store()
      .ui.confirmDialog.confirm(
        new OkCancelDialogOptions(undefined, unsavedDaysWarning(this.dayFormStores))
      )
      .then((res) => {
        if (res === "ok") {
          Timereport_Approve.func({
            timereportId: this.timeReportData.id,
            approvedBy: store().profile.Name
          })
            .then(() => {
              runInAction(() => {
                store().snackbars.success(`Timereport successfully approved`);
                this.fetch(this.bookingId);
                this.deleteRowIfStatusChange(TimeReportStatus.ApprovedByCustomer);

                onSuccess();
              });
            })
            .catch(() => {
              store().snackbars.error(`Cannot approve timereport`);
            })
            .finally(() => {
              this.loadingApprove = false;
            });
        } else if (res === "cancel") {
          this.loadingApprove = false;
        }
      });
  };

  handleSubmitStatus = (onSuccess: () => void) => {
    this.loadingSubmit = true;
    store()
      .ui.confirmDialog.confirm(
        new OkCancelDialogOptions(undefined, unsavedDaysWarning(this.dayFormStores))
      )
      .then((res) => {
        if (res === "ok") {
          Timereport_SetToSubmitted.func({
            timereportId: this.timeReportData.id,
            submittedBy: store().profile.Name
          })
            .then(() => {
              runInAction(() => {
                store().snackbars.success(`Timereport successfully submitted`);
                this.fetch(this.bookingId);
                this.deleteRowIfStatusChange(TimeReportStatus.SubmittedByConsultant);

                onSuccess();
              });
            })
            .catch(() => {
              store().snackbars.error(`Cannot submit timereport`);
            })
            .finally(() => {
              this.loadingSubmit = false;
            });
        } else if (res === "cancel") {
          this.loadingSubmit = false;
        }
      });
  };

  @computed get isDisabledSend() {
    return this.isDeletedByConsultant;
  }

  sendToConsultant = () => {
    if (this.isDisabledSend) {
      return;
    }

    this.loadingEmail = true;
    SendTimeReportToConsultant.func({
      bookingId: this.bookingId,
      timeReportTemplate: this.type
    })
      .then(() => {
        this.toggleEmailDialog();
        store().snackbars.success(`Timereport sent to consultant`);
      })
      .catch((err) => {
        store().snackbars.error(`Cannot send timereport to consultant`);
      })
      .finally(() => {
        this.loadingEmail = false;
      });
  };

  setToDraft = () => {
    if (!this.canSetToDraft) {
      return;
    }

    this.loadingSetDraft = true;

    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions())
      .then((res) => {
        if (res !== "ok") {
          return Promise.resolve() as Promise<any>;
        }

        return Timereport_SetToDraft.func({
          bookingId: this.bookingId,
          approvedBy: store().profile.Name,
          approvedId: store().profile._id
        }).then((result) => {
          runInAction(() => {
            store().snackbars.success(`Tidrapporten har återställts till utkast`);
            this.timeReportData = result.data?.setTimereportToDraft?.timereport;
            this.fetchStatusHistory();
            this.deleteRowIfStatusChange(TimeReportStatus.Draft);
          });
        });
      })
      .catch(() => {
        store().snackbars.error("Cannot update timereport");
      })
      .finally(() => {
        this.loadingSetDraft = false;
      });
  };

  @action.bound setType = (t: TimeReportTemplateType) => {
    this.type = t;
  };

  @action.bound deleteRowIfStatusChange = (status: TimeReportStatus) => {
    // Remove booking week from search result
    const kitFlowSearch = (window as any)?.bookingKitSupportSearchStore;
    const kitFlowFilter = (window as any)?.bookingKitSupportSearchStore?.filterManager;

    if (kitFlowSearch && kitFlowFilter) {
      const timeReportStatus = kitFlowFilter.filter?.timeReportStatus;
      if (timeReportStatus.length > 0 && !timeReportStatus.includes(status)) {
        kitFlowSearch.deleteRowByBookingId(this.bookingId);
      }
      kitFlowSearch.updateRowTimeReportStatusByBookingId(this.bookingId, status);
    }
  };

  @action.bound handleEmail = (options) => {
    return new Promise((resolve, reject) => {
      const { Consultant, Week } = this.bookingInfo;
      const week = moment(Week).format("YYYY-v.w");
      const shortName = `${Consultant.FirstName} ${Consultant.LastName.charAt(0)}`;
      const subject = getSubject(week, shortName);
      const recipients = getRecipients(this.contactPersons);
      const company = this.tenant === "wcc" ? "We Connect Care" : "Bonliva";

      if (recipients.length === 0) {
        // Without an emailable contact the editor opens with an empty "Till" and
        // a disabled send button — a dead end that told the user nothing. Say
        // why here instead; the backend rejects the same case, but only once a
        // request is actually made, and from here none ever is (CRMDEV-7141).
        store().snackbars.error(locale.timereportSendingToCustomer.noCustomerContact);
        resolve(null);
        return;
      }

      store().emailEditor.requestEmailEditor(
        {
          from: replaceEmailDomainByTenant("service@bonliva.se", this.tenant),
          subject,
          body: getBody(week, shortName, company),
          to: [],
          suggestions: recipients,
          attachments: []
        },
        (emailData) => {
          if (!emailData) {
            // user has cancelled the editor
            return Promise.resolve();
          }

          return sendEmail
            .func({
              timeReportId: this.timeReportData.id,
              bookingId: this.bookingInfo._id,
              type: this.type,
              emailInput: {
                from: emailData.from,
                body: emailData.body,
                subject: emailData.subject,
                recipients: emailData.to.map((x) => ({
                  email: x.email,
                  name: x.name
                })),
                attachments: emailData.attachments?.map((x) => x.FileRef.NodeID)
              },
              fileName: subject
            })
            .then(() => {
              store().emailEditor.close();
              store().snackbars.success(locale["Email sent successfully"]);
              this.deleteRowIfStatusChange(
                this.timeReportData.status as unknown as TimeReportStatus
              );
              this.fetchStatusHistory();
              this.reload();
              resolve(null);
            })
            .catch((err) => {
              console.log(err);
              const reason = translateSendTimereportError(err);
              store().snackbars.error(
                `${locale.timereportSendingToCustomer.cannotSendEmail}\n${locale["Reason"]}: ${reason}`
              );
              // The snackbar is gone in seconds; refresh the panel so the failed
              // attempt is still on the booking afterwards (CRMDEV-7141).
              this.fetchStatusHistory();
              reject();
            });
        },
        options
      );
    });
  };
}
